import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COL, type VendorJob, type VendorJobStatus, type VendorSettlement } from '@eprintsmart/shared';
import { auth, db } from '../firebase';

const fns = () => getFunctions(undefined, 'asia-south1');

/**
 * Everything here reads `vendorJobs`, never `orders`. Firestore has no
 * field-level read control, so an order-reading vendor would see what the
 * customer paid — the projection is the boundary, and this service must not
 * route around it.
 */

export async function currentVendorId(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdTokenResult();
  return (token.claims.vendorId as string | undefined) ?? null;
}

export function subscribeToJobs(
  vendorId: string,
  statuses: VendorJobStatus[],
  onChange: (jobs: VendorJob[]) => void,
) {
  return onSnapshot(
    query(
      collection(db, COL.vendorJobs),
      where('vendorId', '==', vendorId),
      where('status', 'in', statuses),
      orderBy('assignedAt', 'desc'),
    ),
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as VendorJob), id: d.id }))),
  );
}

export interface ArtworkDownload {
  id: string;
  filename: string;
  contentType: string;
  bytes: number;
  status: string;
  widthMm: number | null;
  heightMm: number | null;
  url: string;
  expiresAt: number;
}

export async function getArtworkLinks(jobId: string): Promise<ArtworkDownload[]> {
  const { data } = await httpsCallable<{ jobId: string }, { files: ArtworkDownload[] }>(
    fns(),
    'vendorArtworkDownloadUrl',
  )({ jobId });
  return data.files;
}

export async function updateJob(payload: {
  orderId: string;
  orderItemId: string;
  to: VendorJobStatus;
  note?: string;
  courier?: string;
  trackingNumber?: string;
}) {
  return httpsCallable(fns(), 'updateVendorJobStatus')(payload);
}

export interface SettlementView {
  settlement: VendorSettlement;
  lines: {
    jobId: string;
    orderNumber: string;
    productName: string;
    qty: number;
    amountMinor: number;
    dispatchedAt: number | null;
  }[];
}

export async function getSettlement(period?: string): Promise<SettlementView> {
  const { data } = await httpsCallable<{ period?: string }, SettlementView>(
    fns(),
    'getVendorSettlement',
  )({ period });
  return data;
}
